/**
 * Adapters for Super Admin Dashboard
 * Transform backend API data to component-compatible formats
 */

import type { DataTableColumn, DataTableRow, MetricCardProps } from 'finara-components';

import type {
  ClientActivity,
  ProductSummary,
  SuperAdminMetrics,
  AdminMetrics,
  RecentTransaction,
  PendingAction,
  NextPayroll,
  DepartmentDistribution,
} from '@/types/analytics';

// ===== SUPER ADMIN DASHBOARD ADAPTERS =====

/**
 * Transform SuperAdminMetrics to MetricsGrid format
 */
export function adaptSuperAdminMetrics(
  metrics: SuperAdminMetrics
): Record<string, MetricCardProps> {
  return {
    clientesActivos: {
      title: 'Clientes Activos',
      value: metrics.activeClients.total.toString(),
      description: `${metrics.activeClients.inactive} inactivos, ${metrics.activeClients.pending} pendientes`,
      trendingUpText:
        metrics.activeClients.percentageChange > 0
          ? `+${metrics.activeClients.percentageChange}%`
          : undefined,
      trendingDownText:
        metrics.activeClients.percentageChange < 0
          ? `${metrics.activeClients.percentageChange}%`
          : undefined,
    },
    volumenNomina: {
      title: 'Volumen Nómina Hoy',
      value: `$${(metrics.payrollVolume.today / 1000000).toFixed(1)}M`,
      description: `$${(metrics.payrollVolume.thisMonth / 1000000).toFixed(1)}M este mes`,
      trendingUpText:
        metrics.payrollVolume.percentageChangeVsYesterday > 0
          ? `+${metrics.payrollVolume.percentageChangeVsYesterday}%`
          : undefined,
      trendingDownText:
        metrics.payrollVolume.percentageChangeVsYesterday < 0
          ? `${metrics.payrollVolume.percentageChangeVsYesterday}%`
          : undefined,
    },
    speiOut: {
      title: 'SPEI OUT Hoy',
      value: `$${(metrics.speiOut.today / 1000000).toFixed(1)}M`,
      description: `$${(metrics.speiOut.thisMonth / 1000000).toFixed(1)}M este mes`,
      trendingUpText:
        metrics.speiOut.percentageChangeVsYesterday > 0
          ? `+${metrics.speiOut.percentageChangeVsYesterday}%`
          : undefined,
      trendingDownText:
        metrics.speiOut.percentageChangeVsYesterday < 0
          ? `${metrics.speiOut.percentageChangeVsYesterday}%`
          : undefined,
    },
    adelantosActivos: {
      title: 'Adelantos Activos',
      value: `$${(metrics.activeAdvances.totalAmount / 1000000).toFixed(1)}M`,
      description: `${metrics.activeAdvances.recoveryRate.toFixed(1)}% recuperación`,
    },
    saldoTotal: {
      title: 'Saldo Total Administrado',
      value: `$${(metrics.totalBalanceManaged.total / 1000000).toFixed(1)}M`,
      description: metrics.totalBalanceManaged.description,
    },
    errorRate: {
      title: 'Error Rate Global',
      value: `${metrics.globalErrorRate.rate.toFixed(2)}%`,
      description: metrics.globalErrorRate.lastMonth ? 'Último mes' : 'Mes actual',
      trendingUpText:
        metrics.globalErrorRate.percentageChangeVsPreviousMonth > 0
          ? `+${metrics.globalErrorRate.percentageChangeVsPreviousMonth}%`
          : undefined,
      trendingDownText:
        metrics.globalErrorRate.percentageChangeVsPreviousMonth < 0
          ? `${metrics.globalErrorRate.percentageChangeVsPreviousMonth}%`
          : undefined,
    },
  };
}

/**
 * Transform ClientActivity array to DataTable format
 */
export function adaptClientActivityToTable(clients: ClientActivity[]): {
  columns: DataTableColumn[];
  data: DataTableRow[];
} {
  const columns: DataTableColumn[] = [
    { key: 'name', label: 'Cliente', width: '30%' },
    { key: 'totalTransactions', label: 'Transacciones', width: '15%', align: 'center' },
    { key: 'avgTransactionsPerMonth', label: 'Promedio/Mes', width: '15%', align: 'center' },
    { key: 'advancesPercentage', label: '% Adelantos', width: '15%', align: 'center' },
    { key: 'employeesWithAdvances', label: 'Emp. c/Adelantos', width: '15%', align: 'center' },
    { key: 'totalEmployees', label: 'Total Emp.', width: '10%', align: 'center' },
  ];

  const data: DataTableRow[] = clients.map(client => ({
    id: client.id,
    name: client.name,
    totalTransactions: client.totalTransactions.toLocaleString(),
    avgTransactionsPerMonth: client.avgTransactionsPerMonth.toLocaleString(),
    advancesPercentage: `${client.advancesPercentage}%`,
    employeesWithAdvances: client.employeesWithAdvances,
    totalEmployees: client.totalEmployees,
  }));

  return { columns, data };
}

/**
 * Transform ProductSummary to GlobalProductSummary format
 */
export function adaptProductSummary(summary: ProductSummary) {
  return {
    speiIn: {
      totalMonth: summary.speiIn.totalMonth,
      operations: summary.speiIn.operations,
    },
    speiOut: {
      totalMonth: summary.speiOut.totalMonth,
      operations: summary.speiOut.operations,
    },
    totalAccounts: {
      totalBalance: summary.totalAccounts.totalBalance,
      accountsCount: summary.totalAccounts.accountsCount,
    },
    payroll: {
      totalMonth: summary.payroll.totalMonth,
      employeesCount: summary.payroll.employeesCount,
    },
    advances: {
      active: summary.advances.active,
      recoveryRate: summary.advances.recoveryRate,
    },
    cards: {
      issued: summary.cards.issued,
      active: summary.cards.active,
      activePercentage: summary.cards.activePercentage,
    },
    savings: {
      totalInvested: summary.savings.totalInvested,
      returns: summary.savings.returns,
    },
  };
}

// ===== ADMIN DASHBOARD ADAPTERS =====

/**
 * Transform AdminMetrics to MetricsGrid format
 */
export function adaptAdminMetrics(metrics: AdminMetrics): Record<string, MetricCardProps> {
  return {
    activeEmployees: {
      title: 'Empleados Activos',
      value: metrics.activeEmployees.current.toString(),
      description: metrics.activeEmployees.context,
      trendingUpText:
        metrics.activeEmployees.percentageChange > 0
          ? `+${metrics.activeEmployees.percentageChange}%`
          : undefined,
      trendingDownText:
        metrics.activeEmployees.percentageChange < 0
          ? `${metrics.activeEmployees.percentageChange}%`
          : undefined,
    },
    monthlyPayroll: {
      title: 'Nómina Mensual',
      value: `$${metrics.monthlyPayroll.current.toLocaleString()}`,
      description: metrics.monthlyPayroll.context,
      trendingUpText:
        metrics.monthlyPayroll.percentageChange > 0
          ? `+${metrics.monthlyPayroll.percentageChange}%`
          : undefined,
      trendingDownText:
        metrics.monthlyPayroll.percentageChange < 0
          ? `${metrics.monthlyPayroll.percentageChange}%`
          : undefined,
    },
    activeAdvances: {
      title: 'Adelantos Activos',
      value: metrics.activeAdvances.current.toString(),
      description: metrics.activeAdvances.context,
      trendingUpText:
        metrics.activeAdvances.percentageChange > 0
          ? `+${metrics.activeAdvances.percentageChange}%`
          : undefined,
      trendingDownText:
        metrics.activeAdvances.percentageChange < 0
          ? `${metrics.activeAdvances.percentageChange}%`
          : undefined,
    },
    averagePaymentTime: {
      title: 'Tiempo Promedio de Pago',
      value: `${metrics.averagePaymentTime.current} días`,
      description: metrics.averagePaymentTime.context,
      trendingUpText:
        metrics.averagePaymentTime.percentageChange > 0
          ? `+${metrics.averagePaymentTime.percentageChange}%`
          : undefined,
      trendingDownText:
        metrics.averagePaymentTime.percentageChange < 0
          ? `${metrics.averagePaymentTime.percentageChange}%`
          : undefined,
    },
  };
}

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0]?.[0] || ''}${names[1]?.[0] || ''}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

/**
 * Map backend action types to frontend action types
 */
function mapActionType(actionType: string): 'KYC' | 'ADELANTO' | 'BANCARIO' {
  switch (actionType.toLowerCase()) {
    case 'kyc_pending':
    case 'kyc':
      return 'KYC';
    case 'advance_pending':
    case 'adelanto':
      return 'ADELANTO';
    case 'data_change_pending':
    case 'bancario':
      return 'BANCARIO';
    default:
      return 'BANCARIO';
  }
}

/**
 * Transform PendingAction array to component format
 */
export function adaptPendingActions(actions: PendingAction[]): Array<{
  id: string;
  employeeName: string;
  department: string;
  initials: string;
  action: string;
  actionSubtitle: string;
  actionType: 'KYC' | 'ADELANTO' | 'BANCARIO';
}> {
  return actions.map((action, index) => ({
    id: action.employeeId || `action-${index}`,
    employeeName: action.employeeName,
    department: action.department,
    initials: getInitials(action.employeeName),
    action: action.message,
    actionSubtitle: `${action.daysPending} días pendiente`,
    actionType: mapActionType(action.actionType),
  }));
}

/**
 * Map backend transaction types to frontend types
 */
function mapTransactionType(
  type: string
): 'Nómina' | 'Adelanto de Nómina' | 'Bono de Productividad' | 'Reembolso de Gastos' {
  // Map common transaction types to the expected format
  const typeMap: Record<
    string,
    'Nómina' | 'Adelanto de Nómina' | 'Bono de Productividad' | 'Reembolso de Gastos'
  > = {
    Nómina: 'Nómina',
    'Adelanto de Nómina': 'Adelanto de Nómina',
    'Bono de Productividad': 'Bono de Productividad',
    'Reembolso de Gastos': 'Reembolso de Gastos',
    // Add common alternatives
    Adelanto: 'Adelanto de Nómina',
    Bono: 'Bono de Productividad',
    Reembolso: 'Reembolso de Gastos',
  };

  return typeMap[type] || 'Nómina';
}

/**
 * Transform RecentTransaction array to component format
 */
export function adaptRecentTransactions(transactions: RecentTransaction[]): Array<{
  id: string;
  code: string;
  employeeName: string;
  employeeId: string;
  initials: string;
  type: 'Nómina' | 'Adelanto de Nómina' | 'Bono de Productividad' | 'Reembolso de Gastos';
  amount: number;
  status: 'Completado' | 'Procesando' | 'Pendiente';
  createdAt: string;
}> {
  return transactions.map(transaction => ({
    id: transaction.id,
    code: transaction.code,
    employeeName: transaction.employeeName,
    employeeId: transaction.employeeId,
    initials: getInitials(transaction.employeeName),
    type: mapTransactionType(transaction.type),
    amount: transaction.amount,
    status: transaction.status as 'Completado' | 'Procesando' | 'Pendiente',
    createdAt: transaction.createdAt,
  }));
}

/**
 * Transform NextPayroll to component format
 */
export function adaptNextPayroll(payroll: NextPayroll): {
  period: string;
  dateRange: string;
  eligibleEmployees: number;
  estimatedAmount: number;
  archivesStatus: 'Listos' | 'Procesando' | 'Pendiente';
  speiValidation: 'Completada' | 'En progreso' | 'Pendiente';
  dispersionDate: string;
} {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  };

  // Map backend status to frontend status
  const mapStatus = (status: string): 'Listos' | 'Procesando' | 'Pendiente' => {
    switch (status.toLowerCase()) {
      case 'listos':
      case 'ready':
      case 'completed':
        return 'Listos';
      case 'procesando':
      case 'processing':
        return 'Procesando';
      default:
        return 'Pendiente';
    }
  };

  const mapValidation = (status: string): 'Completada' | 'En progreso' | 'Pendiente' => {
    switch (status.toLowerCase()) {
      case 'completada':
      case 'completed':
        return 'Completada';
      case 'en progreso':
      case 'in_progress':
        return 'En progreso';
      default:
        return 'Pendiente';
    }
  };

  return {
    period: 'Quincenal',
    dateRange: `${formatDate(payroll.periodStart)} - ${formatDate(payroll.periodEnd)}`,
    eligibleEmployees: payroll.eligibleEmployees,
    estimatedAmount: payroll.estimatedAmount,
    archivesStatus: mapStatus(payroll.filesStatus),
    speiValidation: mapValidation(payroll.speiValidationStatus),
    dispersionDate: formatDate(payroll.dispersionDate),
  };
}

/**
 * Transform DepartmentDistribution to component format
 */
export function adaptDepartmentDistribution(departments: DepartmentDistribution[]): Array<{
  name: string;
  employeeCount: number;
  monthlyAmount: number;
  color: string;
}> {
  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500'];

  return departments.map((dept, index) => ({
    name: dept.name,
    employeeCount: dept.employeeCount,
    monthlyAmount: dept.avgMonthlySalary * dept.employeeCount,
    color: colors[index % colors.length] || 'bg-gray-500',
  }));
}
